import React, { useState, useEffect, useCallback } from 'react';
import { ApiClient } from '../../api/client';
import {
  ShieldAlert, ShieldCheck, Globe, Lock, Unlock, Wifi,
  AlertTriangle, CheckCircle2, RefreshCw, Plus, Trash2, Eye, EyeOff,
  KeyRound, Users, Server
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// LockdownPanel – Zárt (Vaskapu) üzemmód vezérlőpultja
//
// Superadmin itt látja EGY KÉPERNYŐN, hogy a rendszer kívülről
// elérhető-e, és innen kapcsolhatja a zárt módot, a megbízható
// hálózatokat és a fejlesztői segédleteket – SQL nélkül.
//
// A guardrails 3. pontja miatt MINDEN állapot a szerverről (D1)
// jön és oda is íródik vissza; localStorage-ban semmi nem tárolódik.
// ═══════════════════════════════════════════════════════════════

interface LockdownStatus {
  active: boolean;
  reason: string;
  allow_public_portal: boolean;
  trusted_cidrs: string[];
  activated_by?: number | null;
  activated_at?: string | null;
  your_ip?: string | null;
  failed_closed?: boolean;
}

interface DevFeatures {
  demo_users_endpoint: boolean;
  allow_default_password: boolean;
}

export const LockdownPanel: React.FC = () => {
  const [status, setStatus] = useState<LockdownStatus | null>(null);
  const [dev, setDev] = useState<DevFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);

  // Piszkozat állapot – a mentés csak a gombnyomásra történik
  // (guardrails II: "Gépelés közbeni felesleges mentés").
  const [draftReason, setDraftReason] = useState('');
  const [draftCidrs, setDraftCidrs] = useState<string[]>([]);
  const [newCidr, setNewCidr] = useState('');
  const [allowPublic, setAllowPublic] = useState(true);
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    try {
      // A request<T> egy burkolót ad vissza ({ ok, data?, ...mezők }), a worker
      // pedig lapos JSON-t küld – ezért a válasz mezőit közvetlenül olvassuk.
      const [s, d] = await Promise.all([
        ApiClient.request('/system/lockdown'),
        ApiClient.request('/system/dev-features'),
      ]);
      if (s && typeof s.active === 'boolean') {
        const st: LockdownStatus = {
          active: Boolean(s.active),
          reason: s.reason || '',
          allow_public_portal: s.allow_public_portal !== false,
          trusted_cidrs: Array.isArray(s.trusted_cidrs) ? s.trusted_cidrs : [],
          activated_by: s.activated_by ?? null,
          activated_at: s.activated_at ?? null,
          your_ip: s.your_ip ?? null,
          failed_closed: Boolean(s.failed_closed),
        };
        setStatus(st);
        setDraftReason(st.reason);
        setDraftCidrs(st.trusted_cidrs);
        setAllowPublic(st.allow_public_portal);
      }
      if (d && typeof d.demo_users_endpoint === 'boolean') {
        setDev({
          demo_users_endpoint: Boolean(d.demo_users_endpoint),
          allow_default_password: Boolean(d.allow_default_password),
        });
      }
    } catch (e) {
      console.error(e);
      setFeedback({ ok: false, message: 'Az állapot lekérdezése sikertelen.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCidr = () => {
    const v = newCidr.trim();
    if (!v) return;
    if (!/^\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?$/.test(v)) {
      setFeedback({ ok: false, message: `Érvénytelen IP-tartomány: ${v}` });
      return;
    }
    if (draftCidrs.includes(v)) return;
    setDraftCidrs([...draftCidrs, v]);
    setNewCidr('');
    setFeedback(null);
  };

  const addOwnIp = () => {
    if (!status?.your_ip) return;
    const v = `${status.your_ip}/32`;
    if (!draftCidrs.includes(v)) setDraftCidrs([...draftCidrs, v]);
  };

  const ownIpCovered = () => {
    const ip = status?.your_ip;
    if (!ip) return false;
    return draftCidrs.some(c => c === `${ip}/32` || c.endsWith('/24') || c.endsWith('/16') || c.endsWith('/8'));
  };

  const applyLockdown = async (activate: boolean) => {
    if (activate && confirmText.trim().toUpperCase() !== 'ZÁRÁS') {
      setFeedback({ ok: false, message: 'A bekapcsoláshoz írd be a megerősítő szót: ZÁRÁS' });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const res = await ApiClient.request('/system/lockdown', {
        method: 'POST',
        body: JSON.stringify({
          active: activate,
          confirm: true,
          reason: draftReason,
          allow_public_portal: allowPublic,
          trusted_cidrs: draftCidrs.length ? draftCidrs : ['10.8.0.0/24'],
        }),
      });
      if (res?.ok) {
        setFeedback({ ok: true, message: res.message || 'Beállítás mentve.' });
        setConfirmText('');
        await load();
      } else {
        setFeedback({ ok: false, message: res?.error || 'A művelet nem sikerült.' });
      }
    } catch (e: any) {
      setFeedback({ ok: false, message: e?.message || 'Hálózati hiba.' });
    } finally {
      setBusy(false);
    }
  };

  const toggleDev = async (key: keyof DevFeatures, value: boolean) => {
    setBusy(true);
    try {
      const res = await ApiClient.request('/system/dev-features', {
        method: 'POST',
        body: JSON.stringify({ [key]: value }),
      });
      if (res?.ok) {
        setDev({
          demo_users_endpoint: Boolean(res.demo_users_endpoint),
          allow_default_password: Boolean(res.allow_default_password),
        });
        setFeedback({ ok: true, message: 'Beállítás mentve.' });
      } else {
        setFeedback({ ok: false, message: res?.error || 'A mentés nem sikerült.' });
      }
    } catch (e: any) {
      setFeedback({ ok: false, message: e?.message || 'Hálózati hiba.' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-8 text-slate-500 dark:text-slate-400">
        <RefreshCw className="w-5 h-5 animate-spin" />
        Állapot betöltése...
      </div>
    );
  }

  const isLocked = Boolean(status?.active);

  return (
    <div className="space-y-6 pb-10">
      {/* ── FŐ ÁLLAPOTJELZŐ: kívülről elérhető-e a rendszer? ── */}
      <div
        className={`rounded-2xl border-2 p-6 transition-colors ${
          isLocked
            ? 'border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30'
            : 'border-amber-400/60 bg-amber-50 dark:bg-amber-950/20'
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`shrink-0 rounded-xl p-3 ${
              isLocked ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                       : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            }`}
          >
            {isLocked ? <Lock className="w-7 h-7" /> : <Unlock className="w-7 h-7" />}
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold mb-1">
              {isLocked ? 'ZÁRT ÜZEMMÓD – aktív' : 'NYITOTT ÜZEMMÓD'}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              {isLocked
                ? 'Az adminisztrációs felület kívülről NEM érhető el. Csak a publikus közzétételi oldal és a megbízható (VPN) hálózat.'
                : 'Az adminisztrációs felület és a bejelentkezés jelenleg bárhonnan elérhető az interneten.'}
            </p>

            {/* Két külön sáv: mi látszik kívülről, mi nem */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold mb-1">
                  <Globe className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                  Publikus közzétételi oldal
                </div>
                <div className={`text-sm font-medium ${
                  status?.allow_public_portal !== false
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400'
                }`}>
                  {status?.allow_public_portal !== false
                    ? 'Elérhető a lakosság számára'
                    : 'Nem elérhető'}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold mb-1">
                  <Server className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Admin felület kívülről
                </div>
                <div className={`text-sm font-medium ${
                  isLocked ? 'text-emerald-600 dark:text-emerald-400'
                           : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {isLocked ? 'Zárva (csak VPN)' : 'Nyitva – bárhonnan'}
                </div>
              </div>
            </div>

            {status?.activated_at && isLocked && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                Bekapcsolva: {new Date(status.activated_at).toLocaleString('hu-HU')}
              </p>
            )}

            {status?.failed_closed && (
              <div className="mt-3 flex items-start gap-2 text-sm text-rose-600 dark:text-rose-400">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  A beállítás nem volt olvasható, ezért a rendszer biztonsági okból
                  zártnak tekinti magát (fail-closed).
                </span>
              </div>
            )}
          </div>

          <button
            onClick={load}
            disabled={busy}
            className="shrink-0 rounded-lg border border-slate-300 dark:border-slate-600 p-2 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-50"
            title="Frissítés"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${
          feedback.ok
            ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
        }`}>
          {feedback.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                       : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* ── MEGBÍZHATÓ HÁLÓZATOK ── */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wifi className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold">Megbízható hálózatok (VPN)</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Zárt módban csak ezekről a hálózatokról érhető el az adminisztráció.
          A belső munka innen teljes értékű marad.
        </p>

        {status?.your_ip && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">A te IP-címed:</span>
            <code className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 font-mono">
              {status.your_ip}
            </code>
            {ownIpCovered() ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4" /> a listán szerepel
              </span>
            ) : (
              <button
                onClick={addOwnIp}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-400 px-2 py-1 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40"
              >
                <Plus className="w-3.5 h-3.5" /> Hozzáadom (kizárás elleni védelem)
              </button>
            )}
          </div>
        )}

        <div className="space-y-2 mb-3">
          {draftCidrs.length === 0 && (
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Nincs megadva megbízható hálózat – zárt módban senki nem érné el az admin felületet!
            </p>
          )}
          {draftCidrs.map((c) => (
            <div key={c} className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1.5 font-mono text-sm">
                {c}
              </code>
              <button
                onClick={() => setDraftCidrs(draftCidrs.filter(x => x !== c))}
                className="rounded-lg border border-slate-300 dark:border-slate-600 p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                title="Eltávolítás"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            value={newCidr}
            onChange={(e) => setNewCidr(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCidr(); }}
            placeholder="pl. 10.8.0.0/24"
            className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono"
          />
          <button
            onClick={addCidr}
            className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── KAPCSOLÓ ── */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Zárt üzemmód kapcsolása
        </h3>

        <label className="block text-sm mb-1 text-slate-600 dark:text-slate-400">
          Indoklás (a felhasználók ezt látják)
        </label>
        <input
          value={draftReason}
          onChange={(e) => setDraftReason(e.target.value)}
          placeholder="pl. Éles üzem – zárt mód"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm mb-4"
        />

        <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={allowPublic}
            onChange={(e) => setAllowPublic(e.target.checked)}
            className="rounded"
          />
          <span>
            A publikus közzétételi oldal zárt módban is elérhető maradjon
            <span className="text-slate-500 dark:text-slate-400"> (jogszabályi kötelezettség)</span>
          </span>
        </label>

        {!isLocked ? (
          <>
            <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 mb-4 text-sm">
              <p className="font-semibold mb-1 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Bekapcsolás után:
              </p>
              <ul className="list-disc pl-5 space-y-0.5 text-slate-700 dark:text-slate-300">
                <li>az admin felület és a bejelentkezés csak a fenti hálózatokról lesz elérhető</li>
                <li>a publikus oldal {allowPublic ? 'továbbra is elérhető marad' : 'SZINTÉN NEM lesz elérhető'}</li>
                <li>a saját IP-címed automatikusan bekerül a megbízható listába</li>
              </ul>
            </div>

            <label className="block text-sm mb-1 text-slate-600 dark:text-slate-400">
              Megerősítés – írd be: <code className="font-mono font-bold">ZÁRÁS</code>
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ZÁRÁS"
                className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
              />
              <button
                onClick={() => applyLockdown(true)}
                disabled={busy || draftCidrs.length === 0}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-40"
              >
                <span className="inline-flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Zárt mód bekapcsolása
                </span>
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => applyLockdown(true)}
              disabled={busy}
              className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40"
            >
              Beállítások mentése (zárva marad)
            </button>
            <button
              onClick={() => applyLockdown(false)}
              disabled={busy}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              <span className="inline-flex items-center gap-2">
                <Unlock className="w-4 h-4" /> Zárt mód feloldása
              </span>
            </button>
          </div>
        )}
      </div>

      {/* ── FEJLESZTŐI SEGÉDLETEK ── */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold mb-1 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Fejlesztői segédletek
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Élesben mindkettőt kapcsold ki – információszivárgást okoznak.
        </p>

        <div className="space-y-3">
          <ToggleRow
            icon={dev?.demo_users_endpoint ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            title="Demó gyorsbeléptető lista"
            description="A belépőképernyőn megjelenő fiókválasztó. Bekapcsolva bejelentkezés nélkül is lekérhető minden felhasználó e-mail címe és szerepköre."
            value={Boolean(dev?.demo_users_endpoint)}
            busy={busy}
            onChange={(v) => toggleDev('demo_users_endpoint', v)}
          />
          <ToggleRow
            icon={<Users className="w-4 h-4" />}
            title="Alapértelmezett jelszavas belépés"
            description="Aktiválatlan fiókok beléptetése a gyári jelszóval. Élesben kikapcsolva a rendszer csak annyit közöl: a fiók nincs aktiválva."
            value={Boolean(dev?.allow_default_password)}
            busy={busy}
            onChange={(v) => toggleDev('allow_default_password', v)}
          />
        </div>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        Minden beállítás a szerver adatbázisában tárolódik és minden ki-/bekapcsolás
        bekerül az audit naplóba. Ha a zárt mód mégis kizárna, a szerveren futtatható
        vészkijárat: a <code className="font-mono">lockdown_mode</code> beállítás törlése.
      </p>
    </div>
  );
};

const ToggleRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  value: boolean;
  busy: boolean;
  onChange: (v: boolean) => void;
}> = ({ icon, title, description, value, busy, onChange }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
    <div className={`shrink-0 rounded-lg p-2 ${
      value ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
    }`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm">{title}</div>
      <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{description}</p>
    </div>
    <button
      onClick={() => onChange(!value)}
      disabled={busy}
      className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${
        value
          ? 'bg-amber-500 text-white hover:bg-amber-600'
          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
      }`}
    >
      {value ? 'BE' : 'KI'}
    </button>
  </div>
);
